import { fetchChatList } from '@/api/apiService';
import { Nullable, TypeCall } from '@/types/common';
import { ChatDetailItem } from '@/types/components';
import { convertTimestampToYYMMDDW, convertTimestampToTime } from '@/utils/convertTimestamp.js';

export const useChatDetailList = () => {
  const { t } = useI18n();

  const isLoading = ref<boolean>(false);
  const chatDetailList = ref<ChatDetailItem[]>([]);
  const searchParams = ref({
    keyword: '',
    chatType: '',
  });
  const pagination = ref({
    hasMore: false,
    nextOffset: null,
  });

  const setFilter = async (params) => {
    if (params.keyword !== undefined) {
      searchParams.value.keyword = params.keyword;
    }
    if (params.chatType !== undefined) {
      searchParams.value.chatType = params.chatType;
    }

    await fetchChatDetailList();
  };

  const resetFilter = async () => {
    searchParams.value = {
      keyword: '',
      chatType: '',
    };

    await fetchChatDetailList();
  };

  const fetchChatDetailList = async (offset?: Nullable<string>) => {
    isLoading.value = true;

    try {
      const keyword = searchParams.value.keyword || null;
      const chatType =
        searchParams.value.chatType && searchParams.value.chatType !== 'ALL' ? [searchParams.value.chatType] : null;

      const { data } = await fetchChatList(keyword, offset, false, chatType);

      if (!data || data.datas.length === 0) {
        chatDetailList.value = [];
        pagination.value = {
          hasMore: false,
          nextOffset: null,
        };

        return;
      }

      pagination.value = {
        hasMore: data.hasMore,
        nextOffset: data.nextOffset,
      };

      const formattedList = data.datas.map((item) => {
        return {
          ...item,
          chatName: convertChatName(item.callType, item.chatName),
          chatType: item.callType.toLowerCase(),
          attendees: item.attendees.split(','),
          startedDisplayDate: `${convertTimestampToYYMMDDW(item.startedAt)} ${convertTimestampToTime(item.startedAt)}`,
          endedDisplayDate: item.endedAt
            ? `${convertTimestampToYYMMDDW(item.endedAt)} ${convertTimestampToTime(item.endedAt)}`
            : null,
        };
      });

      if (offset) {
        chatDetailList.value.push(...formattedList);
      } else {
        chatDetailList.value = formattedList;
      }
    } catch (error) {
      console.error('통역 사용 내역 조회 실패:', error);
      chatDetailList.value = [];
    } finally {
      isLoading.value = false;
    }
  };

  const convertChatName = (chatType: TypeCall, chatName: string): string => {
    if (chatType === 'AUDIENCE') return t('label_audience');
    if (chatType === 'SINGLE') return t('label_single');

    return chatName;
  };

  return {
    isLoading,
    chatDetailList,
    searchParams,
    pagination,
    setFilter,
    resetFilter,
    fetchChatDetailList,
  };
};
